import clsx from 'clsx';
import React, { ReactNode } from 'react';

import { Icon } from '~/components/icon/Icon';

import { Button, ButtonProps } from '../button/Button';
import styles from './Alert.module.scss';

export interface AlertProps {
  type?: AlertType;
  variant?: AlertVariant;
  title?: ReactNode;
  message?: ReactNode;
  withIcon?: boolean;
  onClose?: () => void;
  className?: string;
  primaryBtn?: ButtonConfig;
  secondaryBtn?: ButtonConfig;
  customIcon?: ReactNode;
}

interface ButtonConfig extends ButtonProps {
  onClick: () => void;
}

export type AlertVariant = 'inline' | 'toast';

export enum AlertType {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  Info = 'info',
}

const typeClassMap: Record<AlertType, string> = {
  success: styles.typeSuccess,
  warning: styles.typeWarning,
  error: styles.typeError,
  info: styles.typeInfo,
};

const variantClassMap: Record<AlertVariant, string> = {
  inline: styles.variantInline,
  toast: styles.variantToast,
};

const icons = {
  success: (
    <Icon
      fillColor="var(--icon-success)"
      iconName="CheckmarkCircle"
      size={20}
    />
  ),
  warning: (
    <Icon
      fillColor="var(--icon-warning)"
      iconName="ExclamationCircle"
      size={20}
    />
  ),
  error: (
    <Icon fillColor="var(--icon-critical)" iconName="CloseCircle" size={20} />
  ),
  info: (
    <Icon
      fillColor="var(--icon-primary)"
      iconName="InformationCircle"
      size={20}
    />
  ),
};

export const Alert = ({
  type = AlertType.Error,
  variant = 'toast',
  withIcon = true,
  title,
  message,
  onClose,
  className,
  primaryBtn,
  secondaryBtn,
  customIcon,
  ...rest
}: AlertProps) => {
  return (
    <div
      className={clsx(
        styles.alert,
        typeClassMap[type],
        variantClassMap[variant],
        className
      )}
      data-testid="ds-alert"
      data-alert-root
      {...rest}
    >
      {withIcon && (
        <div className={styles.icon} data-testid="icon">
          {customIcon || icons[type]}
        </div>
      )}
      <div className={styles.content} data-alert-content>
        <div className={styles.contentSection}>
          <div className={styles.textWrapper}>
            {title && <p className={styles.title}>{title}</p>}
            {message && (
              <div className={styles.message} data-testid="message">
                {message}
              </div>
            )}
          </div>
          {onClose && (
            <div>
              <button
                className={styles.close}
                data-testid="alert-close-button"
                onClick={onClose}
              >
                <Icon iconName="Close" size={13} />
              </button>
            </div>
          )}
        </div>

        {(primaryBtn || secondaryBtn) && (
          <div className={styles.buttonsContainer} data-alert-buttons>
            {primaryBtn && <Button {...primaryBtn} size="small" />}
            {secondaryBtn && (
              <Button {...secondaryBtn} size="small" variant="secondary" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
